/**
 * Backoffice CRM: System Announcements & App Pause Controller
 * Manages operational real-time system broadcasts and emergency app pause / kill-switch blocking screens.
 */

import { setNavBadge } from "./ui/shell.js";
import { BADGE, BUTTON } from "./ui/classes.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { checkAnnouncementButton } from "./announcementLink.js";

let firestoreCtx = null;
let currentAnnouncementState = null;
let announcementHistory = [];
let unsubscribeRealtime = null;
let currentPreviewTab = "live"; // "live" | "draft"

// The styles' names in the CRM; TYPE_CONFIG below is how the app draws each one.
const TYPE_LABEL = { error: "System down", warning: "Service alert", apology: "Team apology", info: "Announcement" };
const TYPE_BADGE = { error: BADGE.red, warning: BADGE.yellow, apology: BADGE.green, info: BADGE.blue };

const TYPE_CONFIG = {
  error: {
    badge: "SYSTEM DOWN",
    symbol: "⚡",
    themeColor: "#DC2626",
    bgAlpha: "rgba(220, 38, 38, 0.14)",
    glowColor: "rgba(220, 38, 38, 0.25)"
  },
  warning: {
    badge: "SERVICE ALERT",
    symbol: "⚠️",
    themeColor: "#D97706",
    bgAlpha: "rgba(217, 119, 6, 0.14)",
    glowColor: "rgba(217, 119, 6, 0.25)"
  },
  apology: {
    badge: "MESSAGE FROM THE TEAM",
    symbol: "❤️",
    themeColor: "#059669",
    bgAlpha: "rgba(5, 150, 105, 0.14)",
    glowColor: "rgba(71, 148, 107, 0.25)"
  },
  info: {
    badge: "ANNOUNCEMENT",
    symbol: "📣",
    themeColor: "#1E40AF",
    bgAlpha: "rgba(30, 64, 175, 0.14)",
    glowColor: "rgba(30, 64, 175, 0.25)"
  }
};

/**
 * Loads the announcements.html partial template into the container
 */
export async function loadAnnouncementsTemplate() {
  const container = document.getElementById("sectionAnnouncementsContainer");
  if (!container) return;

  try {
    const res = await fetch("templates/announcements.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load announcements.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the Announcements template: ${escapeHTML(err.message)}</div>`;
  }
}

/**
 * Initializes the announcements controller with Firestore bindings. It reads nothing: the template
 * is mounted before anyone has signed in, so app.js calls loadAnnouncements once an admin is in (#1333).
 */
export function initAnnouncementsController(ctx) {
  firestoreCtx = ctx;

  const fieldTitle = document.getElementById("fieldAnnouncementTitle");
  const fieldMessage = document.getElementById("fieldAnnouncementMessage");
  const fieldActionTitle = document.getElementById("fieldActionTitle");
  const fieldActionUrl = document.getElementById("fieldActionUrl");
  const fieldId = document.getElementById("fieldAnnouncementId");
  const btnGenNewId = document.getElementById("btnGenNewId");
  const btnResetForm = document.getElementById("btnResetForm");

  const btnPublish = document.getElementById("btnPublishAnnouncement");
  const btnDeactivateForm = document.getElementById("btnDeactivateForm");
  const btnStopFormAction = document.getElementById("btnStopFormAction");
  const btnStopTop = document.getElementById("btnStopAnnouncementTop");
  const btnResumeTop = document.getElementById("btnResumeAppTop");
  const btnEmergencyPause = document.getElementById("btnEmergencyPause");
  const btnRefresh = document.getElementById("btnRefreshAnnouncement");

  // Tab preview switchers
  const tabPreviewLive = document.getElementById("tabPreviewLive");
  const tabPreviewDraft = document.getElementById("tabPreviewDraft");

  // Presets
  const presetEmergency = document.getElementById("presetEmergency");
  const presetMaintenance = document.getElementById("presetMaintenance");
  const presetApology = document.getElementById("presetApology");
  const presetFeature = document.getElementById("presetFeature");

  // Modal elements
  const btnCancelModal = document.getElementById("btnCancelHardPause");
  const btnConfirmModal = document.getElementById("btnConfirmHardPauseSubmit");

  // History search
  const historySearchInput = document.getElementById("historySearchInput");

  // Preview tab events
  if (tabPreviewLive && tabPreviewDraft) {
    tabPreviewLive.addEventListener("click", () => {
      setPreviewTab("live");
    });
    tabPreviewDraft.addEventListener("click", () => {
      setPreviewTab("draft");
    });
  }

  // Bind live preview listeners on form inputs
  const inputElements = [fieldTitle, fieldMessage, fieldActionTitle, fieldActionUrl];
  inputElements.forEach((el) => {
    if (el) {
      el.addEventListener("input", () => {
        setPreviewTab("draft");
        updateLivePreview();
      });
      el.addEventListener("change", () => {
        setPreviewTab("draft");
        updateLivePreview();
      });
    }
  });

  // Access Mode radio listeners (dismissable vs hard_pause)
  const modeRadios = document.querySelectorAll('input[name="accessMode"]');
  modeRadios.forEach((radio) => {
    radio.addEventListener("change", () => {
      updateAccessModeStyles();
      setPreviewTab("draft");
      updateLivePreview();
    });
  });

  // Type radio listeners
  const typeRadios = document.querySelectorAll('input[name="announcementType"]');
  typeRadios.forEach((radio) => {
    radio.addEventListener("change", () => {
      setPreviewTab("draft");
      updateLivePreview();
    });
  });

  // ID generator
  if (btnGenNewId) {
    btnGenNewId.addEventListener("click", () => {
      generateNewId();
    });
  }

  // Clear / Reset Form
  if (btnResetForm) {
    btnResetForm.addEventListener("click", () => {
      resetFormToBlank();
    });
  }

  // Preset button actions. The three pause presets carry no button: weeknightbiteapp.com has no
  // status page, and which page a pause should offer is Anton's call (#2362). Their button went to
  // status.weeknightbite.com, an unrelated blog's domain (#1426).
  if (presetEmergency) {
    presetEmergency.addEventListener("click", () => {
      applyPreset({
        type: "error",
        mode: "hard_pause",
        title: "System Under Maintenance",
        message: "WeeknightBite is temporarily paused while we upgrade our culinary database and meal planning servers. Access will resume shortly.",
        actionTitle: "",
        actionUrl: ""
      });
    });
  }

  if (presetMaintenance) {
    presetMaintenance.addEventListener("click", () => {
      applyPreset({
        type: "warning",
        mode: "hard_pause",
        title: "Scheduled Maintenance in Progress",
        message: "We are currently applying a critical infrastructure update. App access is temporarily paused. Please check back in a few minutes!",
        actionTitle: "",
        actionUrl: ""
      });
    });
  }

  if (presetApology) {
    presetApology.addEventListener("click", () => {
      applyPreset({
        type: "apology",
        mode: "dismissable",
        title: "We're Back Online!",
        message: "We experienced a brief server slowdown during dinner prep earlier tonight. Everything is now fully restored. Thank you for your patience!",
        actionTitle: "",
        actionUrl: ""
      });
    });
  }

  if (presetFeature) {
    presetFeature.addEventListener("click", () => {
      applyPreset({
        type: "info",
        mode: "dismissable",
        title: "Exciting New Update Available",
        message: "We've added new features to make weeknight dinners smoother than ever. Enjoy faster meal planning and custom recipe notes!",
        actionTitle: "Explore New Features",
        actionUrl: "https://weeknightbiteapp.com"
      });
    });
  }

  // Emergency Pause top bar button
  if (btnEmergencyPause) {
    btnEmergencyPause.addEventListener("click", () => {
      applyPreset({
        type: "error",
        mode: "hard_pause",
        title: "Emergency Service Outage",
        message: "WeeknightBite is temporarily paused while our engineering team resolves an unexpected incident. Access will resume shortly.",
        actionTitle: "",
        actionUrl: ""
      });
      openConfirmationModal();
    });
  }

  // Direct 1-Click Stop Actions
  if (btnStopTop) {
    btnStopTop.addEventListener("click", async () => {
      await deactivateAnnouncement();
    });
  }

  if (btnResumeTop) {
    btnResumeTop.addEventListener("click", async () => {
      await deactivateAnnouncement();
    });
  }

  if (btnStopFormAction) {
    btnStopFormAction.addEventListener("click", async () => {
      await deactivateAnnouncement();
    });
  }

  if (btnDeactivateForm) {
    btnDeactivateForm.addEventListener("click", async () => {
      await deactivateAnnouncement();
    });
  }

  // Refresh button
  if (btnRefresh) {
    btnRefresh.addEventListener("click", async () => {
      await fetchAnnouncementDirect();
      await fetchAnnouncementHistory();
    });
  }

  // Publish button
  if (btnPublish) {
    btnPublish.addEventListener("click", () => {
      const isHardPause = getSelectedMode() === "hard_pause";
      if (isHardPause) {
        openConfirmationModal();
      } else {
        executePublish();
      }
    });
  }

  // Confirmation modal controls
  if (btnCancelModal) {
    btnCancelModal.addEventListener("click", closeConfirmationModal);
  }

  if (btnConfirmModal) {
    btnConfirmModal.addEventListener("click", async () => {
      closeConfirmationModal();
      await executePublish();
    });
  }

  // History search filter
  if (historySearchInput) {
    historySearchInput.addEventListener("input", () => {
      renderHistoryTable();
    });
  }

  // Initialize fresh ID if empty
  if (fieldId && !fieldId.value) {
    generateNewId();
  }
}

/**
 * Subscribes to the live announcement and loads the past ones. app.js calls it once an admin is in,
 * and again at a later sign-in, and runs the function it returns at the sign-out (#1395). A
 * listener still live is replaced.
 */
export function loadAnnouncements() {
  listenToProductionAnnouncement();
  fetchAnnouncementHistory();
  return stopAnnouncementListener;
}

/** Stops the listener on the live announcement, if one is live. */
export function stopAnnouncementListener() {
  if (!unsubscribeRealtime) return;
  unsubscribeRealtime();
  unsubscribeRealtime = null;
}

/**
 * Toggles the preview device between Live in Production and Editor Preview
 */
function setPreviewTab(tab) {
  currentPreviewTab = tab;
  document.getElementById("tabPreviewLive")?.setAttribute("aria-pressed", String(tab === "live"));
  document.getElementById("tabPreviewDraft")?.setAttribute("aria-pressed", String(tab !== "live"));
  updateLivePreview();
}

function generateNewId() {
  const fieldId = document.getElementById("fieldAnnouncementId");
  const now = new Date();
  const dateStr = now.toISOString().split("T")[0].replace(/-/g, "_");
  const randomSuffix = Math.random().toString(36).substring(2, 6);
  if (fieldId) fieldId.value = `announcement_${dateStr}_${randomSuffix}`;
}

function resetFormToBlank() {
  const fieldTitle = document.getElementById("fieldAnnouncementTitle");
  const fieldMessage = document.getElementById("fieldAnnouncementMessage");
  const fieldActionTitle = document.getElementById("fieldActionTitle");
  const fieldActionUrl = document.getElementById("fieldActionUrl");

  if (fieldTitle) fieldTitle.value = "";
  if (fieldMessage) fieldMessage.value = "";
  if (fieldActionTitle) fieldActionTitle.value = "";
  if (fieldActionUrl) fieldActionUrl.value = "";

  generateNewId();
  setSelectedMode("dismissable");
  setSelectedType("info");

  updateFormStatusAlert();
  setPreviewTab("draft");
  updateLivePreview();
  firestoreCtx?.showToast?.("New draft.", "info");
}

/**
 * Subscribes to real-time changes on `/globals/system_announcement`
 */
function listenToProductionAnnouncement() {
  if (!firestoreCtx) return;
  const { db, doc, onSnapshot } = firestoreCtx;
  stopAnnouncementListener();

  try {
    unsubscribeRealtime = onSnapshot(doc(db, "globals", "system_announcement"), (snap) => {
      if (snap.exists()) {
        currentAnnouncementState = { id: snap.id, ...snap.data() };
      } else {
        currentAnnouncementState = null;
      }
      renderProductionStatusBanner();
      updateFormStatusAlert();
      updateLivePreview();
      renderHistoryTable();
    }, (err) => {
      console.warn("⚠️ Error listening to globals/system_announcement:", err.message);
    });
  } catch (err) {
    console.error("Failed to attach announcement listener:", err);
  }
}

/**
 * Direct async fetch fallback
 */
async function fetchAnnouncementDirect() {
  if (!firestoreCtx) return;
  const { db, doc, getDoc, showToast } = firestoreCtx;

  try {
    const snap = await getDoc(doc(db, "globals", "system_announcement"));
    if (snap.exists()) {
      currentAnnouncementState = { id: snap.id, ...snap.data() };
    } else {
      currentAnnouncementState = null;
    }
    renderProductionStatusBanner();
    updateFormStatusAlert();
    updateLivePreview();
    renderHistoryTable();
    if (showToast) showToast("System announcement reloaded from Firestore", "info");
  } catch (err) {
    console.error("Failed to fetch system announcement:", err);
    if (showToast) showToast(`Error fetching announcement: ${err.message}`, "error");
  }
}

// What the status card says in each state; its colours follow data-state in the template.
const STATUS_TEXT = {
  normal: {
    badge: "App normal",
    title: "The app is running normally",
    subtitle: "No announcement or pause screen is showing."
  },
  notice: {
    badge: "Announcement live",
    title: "An announcement is showing",
    subtitle: "Users see it when they open the app and can close it."
  },
  paused: {
    badge: "App paused",
    title: "The app is paused for everyone",
    subtitle: "Users see the pause screen and can't close it or use the app until you resume it."
  }
};

/**
 * Updates the top master status hero bar
 */
function renderProductionStatusBanner() {
  const card = document.getElementById("announcementStatusCard");
  const title = document.getElementById("liveStatusTitle");
  const badge = document.getElementById("liveStatusBadge");
  const subtitle = document.getElementById("liveStatusSubtitle");
  const detailsBox = document.getElementById("liveAnnouncementDetailsBox");
  const detailsTitle = document.getElementById("liveDetailsTitle");
  const detailsMessage = document.getElementById("liveDetailsMessage");
  const lastSynced = document.getElementById("statusLastSynced");

  const btnStopTop = document.getElementById("btnStopAnnouncementTop");
  const btnResumeTop = document.getElementById("btnResumeAppTop");
  const btnEmergency = document.getElementById("btnEmergencyPause");

  const state = currentAnnouncementState;
  const isActive = Boolean(state && state.is_active);
  const isDismissable = state ? Boolean(state.is_dismissable !== false) : true;
  const status = !isActive ? "normal" : isDismissable ? "notice" : "paused";

  if (lastSynced) {
    const now = new Date();
    lastSynced.textContent = `Synced ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }

  // Update Nav Badge: an announcement that cannot be dismissed pauses the app.
  if (isActive) {
    setNavBadge("announcements", isDismissable ? "ACTIVE" : "PAUSED", isDismissable ? "success" : "danger");
  } else {
    setNavBadge("announcements", null);
  }

  if (card) card.dataset.state = status;
  if (badge) badge.textContent = STATUS_TEXT[status].badge;
  if (title) title.textContent = STATUS_TEXT[status].title;
  if (subtitle) subtitle.textContent = STATUS_TEXT[status].subtitle;

  if (detailsBox) {
    detailsBox.style.display = isActive ? "block" : "none";
    if (isActive) {
      if (detailsTitle) detailsTitle.textContent = state.title || "Untitled";
      if (detailsMessage) detailsMessage.textContent = state.message || "—";
    }
  }

  if (btnStopTop) btnStopTop.style.display = status === "notice" ? "inline-flex" : "none";
  if (btnResumeTop) btnResumeTop.style.display = status === "paused" ? "inline-flex" : "none";
  if (btnEmergency) btnEmergency.style.display = status === "paused" ? "none" : "inline-flex";
}

/**
 * Updates status alert box inside the editor
 */
function updateFormStatusAlert() {
  const alertBox = document.getElementById("formStatusAlert");
  const headline = document.getElementById("formStatusHeadline");
  const sub = document.getElementById("formStatusSub");
  const btnStop = document.getElementById("btnStopFormAction");
  const btnDeactivate = document.getElementById("btnDeactivateForm");
  const btnPublishText = document.getElementById("btnPublishText");
  const fieldId = document.getElementById("fieldAnnouncementId");

  const currentId = fieldId?.value?.trim();
  const liveState = currentAnnouncementState;
  const isLiveActive = Boolean(liveState && liveState.is_active);
  const isCurrentLiveMatch = isLiveActive && (liveState.id === currentId);

  if (alertBox) alertBox.dataset.live = String(isCurrentLiveMatch);
  if (headline) headline.textContent = isCurrentLiveMatch ? "Live in the app" : "Draft";
  if (sub) sub.textContent = isCurrentLiveMatch ? "Publishing again updates it on every device at once." : "Users see nothing until you publish.";
  if (btnStop) btnStop.style.display = isCurrentLiveMatch ? "inline-flex" : "none";
  if (btnDeactivate) btnDeactivate.style.display = isCurrentLiveMatch ? "inline-flex" : "none";
  if (btnPublishText) btnPublishText.textContent = isCurrentLiveMatch ? "Update the live announcement" : "Publish to the app";
}

function getSelectedType() {
  const radio = document.querySelector('input[name="announcementType"]:checked');
  return radio ? radio.value : "info";
}

function setSelectedType(type) {
  const radio = document.querySelector(`input[name="announcementType"][value="${type}"]`);
  if (radio) radio.checked = true;
}

/**
 * Access mode helpers: dismissable vs hard_pause
 */
function getSelectedMode() {
  const radio = document.querySelector('input[name="accessMode"]:checked');
  return radio ? radio.value : "dismissable";
}

function setSelectedMode(mode) {
  const radio = document.querySelector(`input[name="accessMode"][value="${mode}"]`);
  if (radio) radio.checked = true;
  updateAccessModeStyles();
}

// The chosen card is drawn by has-checked; the warning and the old checkbox follow the mode.
function updateAccessModeStyles() {
  const isHardPause = getSelectedMode() === "hard_pause";
  const warningBox = document.getElementById("hardLockWarningBox");
  const fieldIsDismissable = document.getElementById("fieldIsDismissable");
  if (fieldIsDismissable) fieldIsDismissable.checked = !isHardPause;
  if (warningBox) warningBox.style.display = isHardPause ? "block" : "none";
}

/**
 * Applies a preset configuration into form fields
 */
function applyPreset(cfg) {
  const fieldId = document.getElementById("fieldAnnouncementId");
  const fieldTitle = document.getElementById("fieldAnnouncementTitle");
  const fieldMessage = document.getElementById("fieldAnnouncementMessage");
  const fieldActionTitle = document.getElementById("fieldActionTitle");
  const fieldActionUrl = document.getElementById("fieldActionUrl");

  if (fieldTitle) fieldTitle.value = cfg.title || "";
  if (fieldMessage) fieldMessage.value = cfg.message || "";
  if (fieldActionTitle) fieldActionTitle.value = cfg.actionTitle || "";
  if (fieldActionUrl) fieldActionUrl.value = cfg.actionUrl || "";

  const now = new Date();
  const dateStr = now.toISOString().split("T")[0].replace(/-/g, "_");
  if (fieldId) fieldId.value = `${cfg.type}_${dateStr}_${Math.random().toString(36).substring(2, 6)}`;

  setSelectedType(cfg.type || "info");
  setSelectedMode(cfg.mode || (cfg.isDismissable === false ? "hard_pause" : "dismissable"));

  updateFormStatusAlert();
  setPreviewTab("draft");
  updateLivePreview();

  firestoreCtx?.showToast?.(`Loaded “${cfg.title}” into the editor.`, "info");
}

/**
 * Updates the interactive iPhone mockup in real time
 */
function updateLivePreview() {
  const normalView = document.getElementById("mockupNormalAppView");
  const modalView = document.getElementById("mockupModalView");
  const badge = document.getElementById("previewModeBadge");

  const mockupRadialGlow = document.getElementById("mockupRadialGlow");
  const mockupIconRing = document.getElementById("mockupIconRing");
  const mockupIconSymbol = document.getElementById("mockupIconSymbol");
  const mockupBadge = document.getElementById("mockupBadge");
  const mockupTitle = document.getElementById("mockupTitle");
  const mockupMessage = document.getElementById("mockupMessage");
  const mockupActionBtn = document.getElementById("mockupActionBtn");
  const mockupActionBtnText = document.getElementById("mockupActionBtnText");
  const mockupDismissBtn = document.getElementById("mockupDismissBtn");
  const mockupLockIndicator = document.getElementById("mockupLockIndicator");

  const isLiveTab = (currentPreviewTab === "live");
  const liveState = currentAnnouncementState;
  const isLiveActive = Boolean(liveState && liveState.is_active);

  if (isLiveTab) {
    if (!isLiveActive) {
      // Show normal app view
      if (normalView) normalView.style.display = "flex";
      if (modalView) modalView.style.display = "none";
      if (badge) {
        badge.textContent = "Live: nothing showing";
        badge.className = BADGE.green;
      }
      return;
    } else {
      // Show live active announcement modal
      if (normalView) normalView.style.display = "none";
      if (modalView) modalView.style.display = "flex";
      if (badge) {
        badge.textContent = "Live in the app";
        badge.className = BADGE.red;
      }

      renderModalContent({
        type: liveState.type || "info",
        title: liveState.title || "Announcement",
        message: liveState.message || "",
        actionTitle: liveState.action_title || "",
        isDismissable: liveState.is_dismissable !== false
      });
      return;
    }
  }

  // DRAFT PREVIEW MODE
  if (normalView) normalView.style.display = "none";
  if (modalView) modalView.style.display = "flex";
  if (badge) {
    badge.textContent = "Draft, not published";
    badge.className = BADGE.paprika;
  }

  const fieldTitle = document.getElementById("fieldAnnouncementTitle");
  const fieldMessage = document.getElementById("fieldAnnouncementMessage");
  const fieldActionTitle = document.getElementById("fieldActionTitle");
  const isDismissable = getSelectedMode() === "dismissable";
  const type = getSelectedType();

  renderModalContent({
    type,
    title: fieldTitle?.value?.trim() || "Announcement Title",
    message: fieldMessage?.value?.trim() || "Detailed explanation of the announcement or system state...",
    actionTitle: fieldActionTitle?.value?.trim() || "",
    isDismissable
  });
}

function renderModalContent({ type, title, message, actionTitle, isDismissable }) {
  const mockupRadialGlow = document.getElementById("mockupRadialGlow");
  const mockupIconRing = document.getElementById("mockupIconRing");
  const mockupIconSymbol = document.getElementById("mockupIconSymbol");
  const mockupBadge = document.getElementById("mockupBadge");
  const mockupTitle = document.getElementById("mockupTitle");
  const mockupMessage = document.getElementById("mockupMessage");
  const mockupActionBtn = document.getElementById("mockupActionBtn");
  const mockupActionBtnText = document.getElementById("mockupActionBtnText");
  const mockupDismissBtn = document.getElementById("mockupDismissBtn");
  const mockupLockIndicator = document.getElementById("mockupLockIndicator");

  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.info;

  if (mockupRadialGlow) {
    mockupRadialGlow.style.background = `radial-gradient(circle at 50% 20%, ${cfg.glowColor}, transparent 70%)`;
  }
  if (mockupIconRing) {
    mockupIconRing.style.background = cfg.bgAlpha;
  }
  if (mockupIconSymbol) {
    mockupIconSymbol.textContent = cfg.symbol;
  }
  if (mockupBadge) {
    mockupBadge.textContent = cfg.badge;
    mockupBadge.style.background = cfg.bgAlpha;
    mockupBadge.style.color = cfg.themeColor;
  }
  if (mockupTitle) {
    mockupTitle.textContent = title;
  }
  if (mockupMessage) {
    mockupMessage.textContent = message;
  }

  if (mockupActionBtn && mockupActionBtnText) {
    if (actionTitle) {
      mockupActionBtn.style.display = "block";
      mockupActionBtn.style.background = cfg.themeColor;
      mockupActionBtn.style.boxShadow = `0 4px 12px ${cfg.glowColor}`;
      mockupActionBtnText.textContent = actionTitle;
    } else {
      mockupActionBtn.style.display = "none";
    }
  }

  if (mockupDismissBtn && mockupLockIndicator) {
    if (isDismissable) {
      mockupDismissBtn.style.display = "block";
      mockupLockIndicator.style.display = "none";
    } else {
      mockupDismissBtn.style.display = "none";
      mockupLockIndicator.style.display = "block";
    }
  }
}

/**
 * The button's title and link as publishing writes them, or null once the admin has been told what
 * to fix: a link that isn't https on our site or a store page never reaches the apps (#1426).
 */
function checkedButton() {
  const fieldActionTitle = document.getElementById("fieldActionTitle");
  const fieldActionUrl = document.getElementById("fieldActionUrl");
  const button = checkAnnouncementButton(fieldActionTitle?.value, fieldActionUrl?.value);
  if (button.ok) return button;
  firestoreCtx?.showToast?.(button.message, "warning");
  (button.field === "title" ? fieldActionTitle : fieldActionUrl)?.focus?.();
  return null;
}

/**
 * Safety confirmation modal handlers. The dialog shows the link users will be sent to, which the
 * phone preview does not.
 */
function openConfirmationModal() {
  const button = checkedButton();
  if (!button) return;

  const modalTitle = document.getElementById("confirmModalTitle");
  const modalType = document.getElementById("confirmModalType");
  const modalButton = document.getElementById("confirmModalButton");
  const modalLink = document.getElementById("confirmModalLink");
  const fieldTitle = document.getElementById("fieldAnnouncementTitle");

  if (modalTitle) modalTitle.textContent = fieldTitle?.value || "System Under Maintenance";
  if (modalType) modalType.textContent = TYPE_LABEL[getSelectedType()] || getSelectedType();
  if (modalButton) modalButton.textContent = button.title || "None";
  if (modalLink) {
    modalLink.textContent = button.url || "None";
    modalLink.dataset.empty = String(!button.url);
  }
  showDialog("modalConfirmHardPause");
}

function closeConfirmationModal() {
  hideDialog("modalConfirmHardPause");
}

/**
 * Publishes the announcement to Cloud Firestore (/globals/system_announcement)
 * and archives a record in (/announcements/{id})
 */
async function executePublish() {
  if (!firestoreCtx) return;
  const { db, doc, setDoc, showToast } = firestoreCtx;

  const fieldId = document.getElementById("fieldAnnouncementId");
  const fieldTitle = document.getElementById("fieldAnnouncementTitle");
  const fieldMessage = document.getElementById("fieldAnnouncementMessage");

  const title = (fieldTitle?.value || "").trim();
  const message = (fieldMessage?.value || "").trim();
  const id = (fieldId?.value || "").trim() || `announcement_${Date.now()}`;
  const isDismissable = getSelectedMode() === "dismissable";
  const type = getSelectedType();
  const nowStr = new Date().toISOString();

  if (!title) {
    if (showToast) showToast("Please provide an announcement title.", "warning");
    fieldTitle?.focus();
    return;
  }

  if (!message) {
    if (showToast) showToast("Please provide an announcement message.", "warning");
    fieldMessage?.focus();
    return;
  }

  // A notice publishes without the dialog, so the link is checked here too.
  const button = checkedButton();
  if (!button) return;

  const payload = {
    id,
    is_active: true,
    is_dismissable: isDismissable,
    type,
    title,
    message,
    action_title: button.title,
    action_url: button.url,
    created_at: nowStr,
    updated_at: nowStr
  };

  try {
    // 1. Write to globals/system_announcement
    await setDoc(doc(db, "globals", "system_announcement"), payload);

    // 2. Archive to announcements/{id}
    await setDoc(doc(db, "announcements", id), {
      ...payload,
      archived_at: nowStr
    });

    if (showToast) {
      if (!isDismissable) {
        showToast("The app is paused for every user.", "error");
      } else {
        showToast("The announcement is live in the app.", "success");
      }
    }

    setPreviewTab("live");
    await fetchAnnouncementHistory();
  } catch (err) {
    console.error("❌ Failed to publish system announcement:", err);
    if (showToast) showToast(`Failed to publish announcement: ${err.message}`, "error");
  }
}

/**
 * Deactivates live broadcast (sets is_active: false in Firestore)
 */
async function deactivateAnnouncement() {
  if (!firestoreCtx) return;
  const { db, doc, setDoc, showToast } = firestoreCtx;

  try {
    const existing = currentAnnouncementState || {};
    const updated = {
      ...existing,
      is_active: false,
      is_dismissable: true,
      updated_at: new Date().toISOString()
    };

    await setDoc(doc(db, "globals", "system_announcement"), updated);

    currentAnnouncementState = updated;
    renderProductionStatusBanner();
    updateFormStatusAlert();
    updateLivePreview();

    if (showToast) showToast("Stopped. The app is back to normal.", "success");
    await fetchAnnouncementHistory();
  } catch (err) {
    console.error("Failed to deactivate announcement:", err);
    if (showToast) showToast(`Failed to stop announcement: ${err.message}`, "error");
  }
}

/**
 * Fetches announcement history from /announcements collection
 */
export async function fetchAnnouncementHistory() {
  if (!firestoreCtx) return;
  const { db, collection, getDocs, query, orderBy, limit } = firestoreCtx;

  try {
    let snap;
    try {
      if (typeof orderBy === "function" && typeof limit === "function") {
        const q = query(collection(db, "announcements"), orderBy("created_at", "desc"), limit(50));
        snap = await getDocs(q);
      } else {
        snap = await getDocs(collection(db, "announcements"));
      }
    } catch {
      snap = await getDocs(collection(db, "announcements"));
    }

    announcementHistory = snap.docs.map((d) => ({
      docId: d.id,
      ...d.data()
    }));

    announcementHistory.sort((a, b) => {
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return timeB - timeA;
    });

    renderHistoryTable();
  } catch (err) {
    console.warn("Could not load /announcements history:", err.message);
    const tbody = document.getElementById("announcementsHistoryTableBody");
    if (tbody) {
      tbody.innerHTML = `<tr><td colspan="6" class="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">No past announcements in <code class="font-mono text-xs">announcements/</code>.</td></tr>`;
    }
  }
}

/**
 * Renders the announcement history table
 */
function renderHistoryTable() {
  const tbody = document.getElementById("announcementsHistoryTableBody");
  const searchInput = document.getElementById("historySearchInput");
  if (!tbody) return;

  const query = (searchInput?.value || "").trim().toLowerCase();

  const filtered = announcementHistory.filter((item) => {
    if (!query) return true;
    const t = (item.title || "").toLowerCase();
    const m = (item.message || "").toLowerCase();
    const id = (item.id || item.docId || "").toLowerCase();
    return t.includes(query) || m.includes(query) || id.includes(query);
  });

  if (filtered.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" class="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">${
      announcementHistory.length ? "No announcement matches the filter." : "No past announcements yet."
    }</td></tr>`;
    return;
  }

  const liveId = currentAnnouncementState?.is_active ? currentAnnouncementState.id : null;

  tbody.innerHTML = filtered.map((item) => {
    const type = item.type || "info";
    const isDismissable = item.is_dismissable !== false;
    const isLive = liveId && (item.id === liveId || item.docId === liveId);

    const dateStr = item.created_at
      ? new Date(item.created_at).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })
      : "—";

    return `
      <tr class="${isLive ? "bg-red-50/60 dark:bg-red-500/5" : ""}">
        <td class="py-3 pr-3 pl-4 text-sm whitespace-nowrap text-gray-500 sm:pl-6 dark:text-gray-400">${escapeHTML(dateStr)}</td>
        <td class="px-3 py-3 text-sm">
          <div class="font-medium text-gray-900 dark:text-white">${escapeHTML(item.title || "Untitled")}</div>
          <div class="font-mono text-xs text-gray-500 dark:text-gray-400">${escapeHTML(item.id || item.docId || "—")}</div>
        </td>
        <td class="px-3 py-3 whitespace-nowrap"><span class="${TYPE_BADGE[type] || BADGE.blue}">${escapeHTML(TYPE_LABEL[type] || type)}</span></td>
        <td class="px-3 py-3 text-sm whitespace-nowrap">${isDismissable
          ? `<span class="text-gray-700 dark:text-gray-300">Close it</span>`
          : `<span class="font-medium text-red-700 dark:text-red-400">Nothing (paused)</span>`}</td>
        <td class="px-3 py-3 whitespace-nowrap">${isLive
          ? `<span class="${BADGE.red}">Live</span>`
          : `<span class="text-sm text-gray-500 dark:text-gray-400">Inactive</span>`}</td>
        <td class="py-3 pr-4 pl-3 text-right whitespace-nowrap sm:pr-6">
          <div class="inline-flex gap-2">
            ${isLive ? `<button type="button" class="btn-stop-row-action ${BUTTON.smallDanger}">Stop</button>` : ""}
            <button type="button" class="load-preset-btn ${BUTTON.small}" data-item-id="${escapeHTML(item.id || item.docId)}">Load</button>
          </div>
        </td>
      </tr>`;
  }).join("");

  // Attach stop button listeners in table
  const stopBtns = tbody.querySelectorAll(".btn-stop-row-action");
  stopBtns.forEach((btn) => {
    btn.addEventListener("click", async () => {
      await deactivateAnnouncement();
    });
  });

  // Attach load button listeners
  const loadBtns = tbody.querySelectorAll(".load-preset-btn");
  loadBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      const itemId = btn.getAttribute("data-item-id");
      const target = announcementHistory.find((x) => (x.id || x.docId) === itemId);
      if (target) {
        applyPreset({
          type: target.type || "info",
          mode: target.is_dismissable === false ? "hard_pause" : "dismissable",
          title: target.title || "",
          message: target.message || "",
          actionTitle: target.action_title || "",
          actionUrl: target.action_url || ""
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  });
}

function escapeHTML(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
